import { datadogRum } from "@datadog/browser-rum";
import { useQueryClient } from "@tanstack/react-query";
import { EHR_SYNC_OUTBOUND_GATED_MESSAGE } from "@utils/ehrActions";
import React, { useEffect, useRef, useState } from "react";

import { Button, Dialog, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  FronteraEhrLearnerExportStatusEnum,
  ReportFronteraEhrClientExportQuery,
  ReportTypeEnums,
  SendClientToFronteraEhrMutation,
  useReportFronteraEhrClientExportQuery,
  useSendClientToFronteraEhrMutation,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import {
  exportNodeOf,
  fronteraEhrClientExportQueryKey,
  pollWhileExportPending,
} from "@components/SendClientToFronteraEhr/fronteraEhrClientExport";
import { notifyError, notifySuccess, notifyWarn } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

export const SEND_CLIENT_TO_FRONTERA_EHR_TEXT = "Send client to Frontera EHR";
export const CLIENT_ALREADY_IN_FRONTERA_EHR_TEXT = "This client is already in Frontera EHR";
export const SENDING_TO_FRONTERA_EHR_TEXT = "Sending to Frontera EHR…";
const GENERIC_ERROR_TEXT = "We couldn't send this client to Frontera EHR. Please try again.";
const SENDING_SUCCESS_TEXT = "Sending client to Frontera EHR.";
const PLAIN_SENT_MESSAGE = "Client sent to Frontera EHR";

interface SendClientToFronteraEhrButtonProps {
  reportId: string;
  reportType: ApiReportReportTypeChoices | null | undefined;
  isReportCompleted: boolean;
  learnerExternalId: string | null | undefined;
}

type ExportNode = NonNullable<
  NonNullable<NonNullable<ReportFronteraEhrClientExportQuery["getReports"]>["edges"][number]>["node"]
>;
type SendReply = SendClientToFronteraEhrMutation["sendClientToFronteraEhr"];

const seededExport = (
  previous: ReportFronteraEhrClientExportQuery | undefined,
  reportId: string,
  reply: NonNullable<SendReply>,
): ReportFronteraEhrClientExportQuery => {
  const node = exportNodeOf(previous);
  const seeded: ExportNode = {
    id: node?.id ?? reportId,
    learner: reply.learner ?? node?.learner ?? null,
    fronteraEhrClientExport: {
      status: reply.exportStatus ?? FronteraEhrLearnerExportStatusEnum.Sent,
      message: reply.message ?? "",
    },
  };
  return { getReports: { edges: [{ node: seeded }] } };
};

const notifySettledExport = (status: FronteraEhrLearnerExportStatusEnum | undefined, message: string | undefined) => {
  if (status === FronteraEhrLearnerExportStatusEnum.Failed) {
    notifyError(message || GENERIC_ERROR_TEXT);
  } else if (status === FronteraEhrLearnerExportStatusEnum.Sent && message && message !== PLAIN_SENT_MESSAGE) {
    notifyWarn(message);
  }
};

const buttonTooltip = ({
  isAlreadyInFronteraEhr,
  isEhrSyncCleared,
  isFailed,
  exportStatus,
  exportMessage,
}: {
  isAlreadyInFronteraEhr: boolean;
  isEhrSyncCleared: boolean;
  isFailed: boolean;
  exportStatus: FronteraEhrLearnerExportStatusEnum | undefined;
  exportMessage: string | undefined;
}): string | null => {
  if (isAlreadyInFronteraEhr) {
    const sentNote =
      exportStatus === FronteraEhrLearnerExportStatusEnum.Sent && exportMessage && exportMessage !== PLAIN_SENT_MESSAGE
        ? exportMessage
        : null;
    return sentNote ? `${CLIENT_ALREADY_IN_FRONTERA_EHR_TEXT}. ${sentNote}` : CLIENT_ALREADY_IN_FRONTERA_EHR_TEXT;
  }
  if (!isEhrSyncCleared) return `${EHR_SYNC_OUTBOUND_GATED_MESSAGE} Contact support to get started.`;
  return isFailed && exportMessage ? exportMessage : null;
};

export const SendClientToFronteraEhrButton: React.FC<SendClientToFronteraEhrButtonProps> = ({
  reportId,
  reportType,
  isReportCompleted,
  learnerExternalId,
}) => {
  const { isFronteraEhrUser, isEhrSyncCleared } = useAdminData();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Render-closure `isPending` lets two clicks in one frame both send.
  const sendingRef = useRef(false);
  const { isPending, mutateAsync } = useSendClientToFronteraEhrMutation({});

  const isShown =
    isFronteraEhrUser && reportType === ApiReportReportTypeChoices.DiagnosisEvaluation && isReportCompleted;
  const queryKey = fronteraEhrClientExportQueryKey(reportId);
  const exportQuery = useReportFronteraEhrClientExportQuery(
    { reportId, filter: { reportType: ReportTypeEnums.DiagnosisEvaluation } },
    { queryKey, enabled: isShown, refetchInterval: pollWhileExportPending },
  );
  const exportNode = exportNodeOf(exportQuery.data);
  const exportStatus = exportNode?.fronteraEhrClientExport?.status;
  const exportMessage = exportNode?.fronteraEhrClientExport?.message;

  // The report whose send outcome is unknown, including a PENDING reply whose refetch settles before it renders.
  const awaitingOutcomeRef = useRef<string | null>(null);
  useEffect(() => {
    if (exportStatus === FronteraEhrLearnerExportStatusEnum.Pending) {
      awaitingOutcomeRef.current = reportId;
      return;
    }
    if (awaitingOutcomeRef.current !== reportId || !exportStatus) return;
    awaitingOutcomeRef.current = null;
    notifySettledExport(exportStatus, exportMessage);
  }, [reportId, exportStatus, exportMessage, exportQuery.dataUpdatedAt]);

  if (!isShown) return null;

  const isAlreadyInFronteraEhr =
    !!(exportNode?.learner?.externalId ?? learnerExternalId) ||
    exportStatus === FronteraEhrLearnerExportStatusEnum.Sent;
  const isSending = !isAlreadyInFronteraEhr && exportStatus === FronteraEhrLearnerExportStatusEnum.Pending;
  const disabled = isAlreadyInFronteraEhr || !isEhrSyncCleared || isPending || isSending;

  const tooltipText = buttonTooltip({
    isAlreadyInFronteraEhr,
    isEhrSyncCleared,
    isFailed: exportStatus === FronteraEhrLearnerExportStatusEnum.Failed,
    exportStatus,
    exportMessage,
  });

  const handleReply = (reply: SendReply) => {
    if (!reply?.status) {
      notifyError(reply?.message || GENERIC_ERROR_TEXT);
      return;
    }
    awaitingOutcomeRef.current = reply.exportStatus === FronteraEhrLearnerExportStatusEnum.Pending ? reportId : null;
    queryClient.setQueryData<ReportFronteraEhrClientExportQuery>(queryKey, (previous) =>
      seededExport(previous, reportId, reply),
    );
    notifySuccess(reply.message || SENDING_SUCCESS_TEXT);
  };

  const sendClient = async () => {
    if (disabled || sendingRef.current) return;
    sendingRef.current = true;
    try {
      const data = await mutateAsync({ reportId });
      handleReply(data?.sendClientToFronteraEhr);
    } catch (error) {
      datadogRum.addError(error, { context: "Error sending client to Frontera EHR" });
      notifyError(GENERIC_ERROR_TEXT);
    } finally {
      sendingRef.current = false;
    }
    setDialogOpen(false);
    void queryClient.invalidateQueries({ queryKey });
  };

  const button = (
    <Button
      text={
        <div className="flex items-center gap-1">
          {isPending || isSending ? <Loader className="h-4 w-4" /> : null}
          {isSending ? SENDING_TO_FRONTERA_EHR_TEXT : SEND_CLIENT_TO_FRONTERA_EHR_TEXT}
        </div>
      }
      disabled={disabled}
      appearance="primary"
      onClick={() => setDialogOpen(true)}
      className="ring-green-400"
    />
  );

  return (
    <>
      <div className="inline-block ml-2">
        {tooltipText ? (
          <Tooltip text={tooltipText} showInfoIcon={false}>
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </div>
      <Dialog
        size="xl"
        open={dialogOpen}
        setOpen={setDialogOpen}
        title={SEND_CLIENT_TO_FRONTERA_EHR_TEXT}
        primaryButton={{
          text: "Continue",
          onClick: sendClient,
          disabled: isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setDialogOpen(false),
        }}
      >
        <>
          <Paragraph displayType="normal" colorType="secondary">
            This will send the client&apos;s details to Frontera EHR: name, date of birth, gender, guardians and home
            address when present.
          </Paragraph>
          <Paragraph displayType="normal" colorType="secondary" className="my-4">
            A new client will be created in the Unassigned clinic for your staff to assign. Would you like to proceed?
          </Paragraph>
        </>
      </Dialog>
    </>
  );
};
